<template>
    <div class="core-currency-input-wrapper">
        <InputNumber
            ref="inputNumber"
            v-model="inputValue"
            mode="currency"
            :currency="currencyCode"
            :locale="numberFormat()"
            :name="name"
            :inputStyle="{ 'text-align': textAlign }"
            :maxFractionDigits="maxFractionDigits"
            :class="classes"
            :disabled="disabled"
            :placeholder="placeholder"
            @focus="onFocus()"
            @blur="onBlur()"
            @input="onInput"
        />
        <CoreInputNotification
            v-if="isFocused"
            :validator="validator"
            :customNotificationMessage="customNotificationMessage"
        />
    </div>
</template>

<script>
import { useUserStore } from '@/stores/user'
import InputNumber from 'primevue/inputnumber'
import CoreInputNotification from './CoreInputNotification.vue'
import { numberFormatPlaceholder } from '@/helpers/localeOptions'
import { parsePastedNumber } from '@/helpers/pastedNumber'

export default {
    name: 'CoreCurrencyInput',

    components: { InputNumber, CoreInputNotification },

    props: {
        modelValue: {
            type: [String, Number],
            default: null,
        },
        showBorder: {
            type: Boolean,
            default: true,
        },
        name: {
            type: String,
            default: '',
        },
        maxFractionDigits: {
            type: Number,
            default: 2,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        condensed: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        validator: {
            type: Object,
            default: null,
        },
        customNotificationMessage: {
            type: Object,
            default: null,
        },
        currency: {
            type: String,
            default: null,
        },
        textAlign: {
            type: String,
            default: 'right',
        },
    },

    emits: ['update:modelValue', 'input', 'focus', 'blur'],

    data() {
        return {
            isFocused: false,
            userStore: useUserStore(),
        }
    },

    mounted() {
        if (this.validator) {
            if (this.modelValue === null || this.modelValue === '') {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }

        this.$el.addEventListener('paste', this.onPaste, true)
    },

    beforeUnmount() {
        this.$el.removeEventListener('paste', this.onPaste, true)
    },

    computed: {
        inputValue: {
            get() {
                const val = this.modelValue
                return isNaN(parseFloat(val)) ? val : parseFloat(val)
            },
            set(value) {
                this.$emit('update:modelValue', value ? value.toString() : value)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        classes() {
            return {
                'core-currency-input': true,
                'p-inputnumber-input--no-border': !this.showBorder,
                'core-currency-input--full-width': this.fullWidth,
                'core-currency-input--condensed': this.condensed,
                'core-valid': this.isValid,
                'core-invalid': this.isInvalid,
                'core-warning': this.isWarning,
            }
        },

        isValid() {
            if (this.validator !== null && !this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isInvalid() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'error'
            ) {
                return true
            }

            if (this.validator !== null && this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isWarning() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'warn'
            ) {
                return true
            }

            return false
        },

        validatorErrors() {
            if (this.validator?.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator?.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },

        currencyCode() {
            return this.currency || this.userStore.userData.currency_code || 'EUR'
        },

        currencySymbol() {
            const currencyCode = this.currencyCode
            const allowedCurrencies = this.userStore.userData.allowed_currency_options
            const currency = allowedCurrencies.find((currency) => currency.value === currencyCode)
            return currency ? currency.symbol : '€'
        },

        placeholder() {
            const placeholder_tk_name = numberFormatPlaceholder(
                this.userStore.userData.preferred_number_format,
            )
            const currency_symbol = this.currencySymbol
            const placeholder = this.$t(placeholder_tk_name, { currency_symbol })

            if (!this.displayedDecimals) {
                return placeholder
            }

            // The translation shows a whole 0: show it with the decimals the field shows (0,00 €)
            const zero = new Intl.NumberFormat(this.numberFormat(), {
                minimumFractionDigits: this.displayedDecimals,
                maximumFractionDigits: this.displayedDecimals,
            }).format(0)

            return placeholder.replace(/(?<!\d)0(?!\d)/, zero)
        },

        // Decimals InputNumber shows for a whole number: the currency's own (2 for EUR, 0 for JPY),
        // capped at maxFractionDigits
        displayedDecimals() {
            try {
                return new Intl.NumberFormat(this.numberFormat(), {
                    style: 'currency',
                    currency: this.currencyCode,
                    maximumFractionDigits: this.maxFractionDigits,
                }).resolvedOptions().minimumFractionDigits
            } catch {
                return 0
            }
        },
    },

    methods: {
        numberFormat() {
            return this.userStore.userData.preferred_number_format
        },

        onFocus() {
            this.isFocused = true
            this.$emit('focus')
        },

        onBlur() {
            this.isFocused = false
            this.$emit('blur')
        },

        // PrimeVue updates the model only on blur, Enter/Tab and spin; this fires on every keystroke
        onInput(event) {
            this.$emit('input', event.value)
        },

        onPaste(event) {
            const inputNumber = this.$refs.inputNumber
            if (
                typeof inputNumber?.insert !== 'function' ||
                inputNumber.readonly ||
                inputNumber.disabled
            ) {
                return
            }

            const text = event.clipboardData?.getData('text') ?? ''
            const value = parsePastedNumber(
                text,
                this.numberFormat(),
                this.currencyCode,
                this.maxFractionDigits,
            )

            if (value === null) {
                if (inputNumber.parseValue?.(text) != null) {
                    this.selectAll(event.target)
                }
                return
            }

            event.preventDefault()
            event.stopPropagation()
            this.selectAll(event.target)
            inputNumber.insert(event, value.toString())
        },

        selectAll(input) {
            input.setSelectionRange(0, input.value.length)
        },
    },
}
</script>

<style>
.core-currency-input-wrapper {
    position: relative;
    width: 100%;

    & > span {
        width: 100%;
    }

    & .p-inputtext.p-component.p-inputnumber-input {
        font-size: var(--text-base);
    }

    & > span.core-currency-input > input {
        width: 100%;
    }

    & > span.core-currency-input > input:disabled {
        background-color: var(--color-disabled-bg);
        color: var(--text-disabled);
    }

    & .core-currency-input.core-valid input,
    & .core-currency-input.core-valid input:focus,
    & .core-currency-input.core-valid input:hover {
        border-color: var(--color-success);
    }

    & .core-currency-input.core-invalid input,
    & .core-currency-input.core-invalid input:focus,
    & .core-currency-input.core-invalid input:hover {
        border-color: var(--color-error);
    }

    & .core-currency-input.core-warning input,
    & .core-currency-input.core-warning input:focus,
    & .core-currency-input.core-warning input:hover {
        border-color: var(--color-warning);
    }
}
</style>
